'use client';

import { useState } from 'react';
import { BookOpen } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { BoardroomExplainerModal } from '@/components/boardroom/boardroom-explainer-modal';
import { cn } from '@/lib/utils';

interface FaqDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Field Manual — the in-app FAQ.
 *
 * Single-column scrollable dialog modelled on `TermsDialog`. Each
 * Q&A is a section: amber question heading + readable zinc body,
 * separated by hairline dividers so the rhythm stays calm and the
 * copy is the design. Anything more decorative (plinth gradients,
 * accordion chrome, "1-of-1" badges) was actively fighting the rest
 * of the lobby — we let the typography carry it instead.
 */
export function FaqDialog({ open, onOpenChange }: FaqDialogProps) {
  const [boardroomExplainerOpen, setBoardroomExplainerOpen] = useState(false);
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl bg-[#0b0a08] border-amber-500/30 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="text-amber-100 flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              Field Manual
            </DialogTitle>
            <DialogDescription className="text-zinc-400 text-[12px]">
              Everything you need to know about owning land
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[65vh] overflow-y-auto pr-2 -mr-2 divide-y divide-amber-500/10">
            <Section question="Why should I own land?">
              <p>
                Owning a tile gets you{' '}
                <Strong>access to the board's chat</Strong> and a slice
                of <Strong>protocol-yield rewards</Strong> — paid in
                USDC, shared pro-rata across active seat holders.
              </p>
              <p>
                The cost is two parts: an upfront <Strong>deposit</Strong>{' '}
                and a continuous <Strong>per-week Harberger tax</Strong>{' '}
                drawn from that deposit. You set your seat's price —
                higher price means more tax burn, but more upside if
                someone buys you out.
              </p>
              <p className="text-zinc-400">
                The balance: pick a price you'd genuinely be happy to
                sell at. Too high and you burn deposit fast; too low
                and you'll get bought out before the rewards land.
              </p>
            </Section>

            <Section question="What is the Boardroom?">
              <p>
                The Boardroom is the protocol's flagship board. Its
                seat holders earn the protocol's pooled yield — paid in{' '}
                <Strong>USDC</Strong>, just like every other board.
              </p>
              <p>
                What makes it different: the Boardroom's own Harberger
                tax is collected in <Strong>$TAKEOVER</Strong>, not
                USDC. It's the only board where you pay tax in the
                protocol token — buying a seat means converting upside
                in TAKEOVER into ongoing USDC yield.
              </p>
              <button
                type="button"
                onClick={() => {
                  onOpenChange(false);
                  setBoardroomExplainerOpen(true);
                }}
                className="text-amber-300 underline underline-offset-4 hover:text-amber-200 transition-colors"
              >
                Watch the animated explainer →
              </button>
            </Section>

            <Section question="What's the tax on land?">
              <p>
                Each board sets its own per-week Harberger tax.
                Creators pick anywhere from <Strong>1%</Strong> to{' '}
                <Strong>10%</Strong> at launch, with{' '}
                <Strong>5%/wk</Strong> as the default — you'll see the
                exact rate on every board's metadata card (the{' '}
                <code className="text-amber-200/80">%/wk tax</code>{' '}
                chip).
              </p>
              <p>
                Tax accrues continuously and is drawn from your seat's
                deposit. Top up before the runway hits zero or you'll
                be eligible for forfeit.
              </p>
            </Section>

            <Section question="What happens at forfeit?">
              <p>
                Forfeit happens when your deposit runs out and you
                stop paying tax. What happens next depends on which
                UGM version the board runs on — visible as the{' '}
                <Strong>Dutch forfeit</Strong> /{' '}
                <Strong>Instant claim</Strong> chip on every board's
                metadata card.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                <div className="rounded-md border border-zinc-700/60 bg-zinc-900/40 p-3.5">
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-400 mb-2">
                    v2.1 · Instant claim
                  </div>
                  <p className="text-[12.5px] text-zinc-300 leading-relaxed">
                    The seat drops straight to free-claim. The next
                    caller can take it for the deposit floor — your
                    listed price is wiped. Includes the Boardroom.
                  </p>
                </div>
                <div className="rounded-md border border-amber-500/35 bg-amber-950/15 p-3.5">
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-amber-200 mb-2">
                    v2.2 · Dutch forfeit
                  </div>
                  <p className="text-[12.5px] text-zinc-200 leading-relaxed">
                    The seat enters a Dutch auction at your last price,
                    decaying to 0 over the board's forfeiture window.
                    Protocol takes a 5% penalty on the clearing price —
                    use <Strong>Abandon</Strong> to dodge it.
                  </p>
                </div>
              </div>
            </Section>

            <Section question="Can I transfer land?">
              <p>
                Seats are <Strong>non-transferable</Strong>. There's no{' '}
                <code className="text-amber-200/80">safeTransferFrom</code>{' '}
                — the only way to hand a position over is the open
                market.
              </p>
              <p>
                To exit voluntarily, hit <Strong>Abandon</Strong> in
                the manage drawer: your remaining deposit (minus
                accrued tax) is refunded and the seat returns to the
                next claimer or buyer.
              </p>
            </Section>

            <Section question="Where do my rewards come from?">
              <p>
                Each board has a yield adapter — creator-coin trading
                fees, protocol revenue, etc. — that funnels into a
                USDC pool shared by active seat holders.
              </p>
              <p>
                Rewards split pro-rata across the active seats. Claim
                from the <Strong>Rewards</Strong> pill in the lobby
                header (or the Rewards tab in the bottom nav on
                mobile).
              </p>
            </Section>

            <Section question="My deposit was fine — why was my land taken?">
              <p>
                Harberger pricing: anyone can buy your seat at the
                price <em>you</em> set, at any time. If you under-priced,
                a buyer can take it from under you.
              </p>
              <p>
                When that happens your remaining deposit (minus
                accrued tax) is refunded in the same transaction. Set
                a price you'd genuinely sell at — that's the whole
                point.
              </p>
            </Section>

            <Section question="Price vs. deposit — what's the difference?">
              <p>
                <Strong>Price</Strong> is what someone else pays to
                buy your seat. Higher price means more upside if you're
                bought out, but more weekly tax burn.
              </p>
              <p>
                <Strong>Deposit</Strong> is your runway — the pot tax
                is drawn from. Top it up to extend the time before
                forfeit.
              </p>
            </Section>

            <Section question="What chains and wallets work?">
              <p>
                Takeover lives on <Strong>Base</Strong> only. Connect
                any EVM wallet — MetaMask, Rabby, Coinbase Wallet,
                Rainbow — or sign in with email, Farcaster, or Twitter
                via the embedded wallet flow.
              </p>
            </Section>
          </div>
        </DialogContent>
      </Dialog>

      <BoardroomExplainerModal
        open={boardroomExplainerOpen}
        onOpenChange={setBoardroomExplainerOpen}
      />
    </>
  );
}

function Section({
  question,
  children,
}: {
  question: string;
  children: React.ReactNode;
}) {
  return (
    <section className="py-5 first:pt-2 space-y-3">
      <h3 className="text-[15px] font-semibold text-amber-100 leading-snug">
        {question}
      </h3>
      <div className="space-y-2.5 text-[13px] text-zinc-300 leading-[1.65]">
        {children}
      </div>
    </section>
  );
}

function Strong({ children }: { children: React.ReactNode }) {
  return (
    <strong className={cn('font-semibold text-amber-100')}>{children}</strong>
  );
}
